import { collectionAllowed } from './analyticsPolicy';
import { analyticsEvent } from './siteAnalytics';
import { primaryEndpoint } from './askSite';
import { newId, sessionId } from './askTelemetry';
import { createVisitRecorder } from './visitRecorder';

const VISITOR_KEY = 'site_visitor:v1';
let memoryVisitor = '', recorder = null, page = null, surfaces = [], startTimer, scrollMilestone = 0;
const clean = (value, max = 120) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, max);
export function activityEnabled() { return collectionAllowed(); }
export function visitorMetadata() {
  if (!activityEnabled()) return {};
  if (!memoryVisitor) {
    try {
      const saved = JSON.parse(localStorage.getItem(VISITOR_KEY) || 'null');
      if (saved?.id && Date.now() - saved.created < 180 * 86400000) memoryVisitor = saved.id;
      else { memoryVisitor = newId(); localStorage.setItem(VISITOR_KEY, JSON.stringify({ id: memoryVisitor, created: Date.now() })); }
    } catch { memoryVisitor = newId(); }
  }
  let referrer = '';
  try { referrer = document.referrer ? new URL(document.referrer).origin : ''; } catch { /* No referrer supplied. */ }
  return { visitor_id: memoryVisitor, session_id: sessionId(), referrer, path: page?.path || '/' };
}
function activeContext() { return surfaces[surfaces.length - 1]?.context || page; }
function sync() {
  if (!activityEnabled()) { recorder?.discard(); return; }
  recorder?.setContext(activeContext());
}
export function activityAction(action, target, detail) {
  if (!activityEnabled()) return;
  recorder?.action(action, clean(target), clean(detail, 240));
  const names = { reading_mode: 'reading_mode', afterword_reached: 'exhibition_complete' };
  if (names[action]) analyticsEvent(names[action], { presentation: activeContext()?.presentation });
  if (action === 'ticket' && /open/i.test(target)) analyticsEvent('ticket_open');
  if (action === 'tour' && /start/i.test(target)) analyticsEvent('gallery_tour');
  if (action === 'audio' && /^play/i.test(target)) analyticsEvent('audio_play', { presentation: activeContext()?.presentation });
}
export function updatePageActivity(context) {
  if (page) { page = { ...page, ...context }; sync(); }
}
export function openActivitySurface(context) {
  const surface = { context: { ...page, ...context } };
  surfaces.push(surface); sync();
  return {
    update(next) { if (!surfaces.includes(surface)) return; surface.context = { ...surface.context, ...next }; sync(); },
    action: activityAction,
    close() { surfaces = surfaces.filter(item => item !== surface); sync(); },
  };
}
export function setActivityPreference(enabled) {
  try { localStorage.setItem('askTelemetry', enabled ? 'on' : 'off'); } catch { /* Storage may be unavailable. */ }
  window.dispatchEvent(new Event('analytics-preference'));
  sync();
}

export function startSiteActivity(router) {
  if (typeof window === 'undefined' || recorder) return;
  recorder = createVisitRecorder({ id: newId, identity: visitorMetadata, isVisible: () => document.visibilityState !== 'hidden',
    async send(body, beacon) {
      if (!activityEnabled() || !primaryEndpoint()) return false;
      const url = `${primaryEndpoint()}/activity`, payload = JSON.stringify(body);
      // Each packet is bounded to ten stops and twenty actions, under beacon's
      // 64 KB limit. No DOM snapshots, input values or coordinates are sent.
      if (beacon && navigator.sendBeacon?.(url, new Blob([payload], { type: 'text/plain;charset=UTF-8' }))) return true;
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(url, { method: 'POST', body: payload, headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, keepalive: true, signal: controller.signal });
        return response.ok;
      } finally { clearTimeout(timeout); }
    },
  });
  window.addEventListener('analytics-preference', sync);
  window.addEventListener('storage', sync);
  window.addEventListener('site-analytics-action', event => {
    const names = { site_search: 'search', site_ask: 'ask', view_composed: 'view_compose' };
    const action = names[event.detail?.name];
    if (action) activityAction(action, event.detail.parameters?.surface || 'site', event.detail.parameters?.outcome || 'complete');
  });
  if (activityEnabled()) import('web-vitals').then(({ onCLS, onINP, onLCP }) => {
    const record = ({ name, value }) => activityAction('performance', name, String(Math.round(value * 1000) / 1000));
    onCLS(record); onINP(record); onLCP(record);
  }).catch(() => {});
  function route(to) {
    if (!to) return;
    recorder.flush();
    surfaces = []; scrollMilestone = 0;
    const path = to.path + (to.hash || '');
    page = { path, title: clean(to.meta?.title || document.title, 180), share_id: /^\/view\/([^/]+)$/.exec(to.path)?.[1] || null, presentation: 'page', place: to.path.startsWith('/view/') ? 'Loading View' : '' };
    sync();
    activityAction('page_open', to.path.startsWith('/view/') ? '/view' : to.path);
    clearTimeout(startTimer); startTimer = setTimeout(() => recorder.flush(), 1200);
  }
  router.afterEach((to, from, failure) => { if (!failure) route(to); });
  router.isReady().then(() => { if (!page) route(router.currentRoute.value); });
  setInterval(() => { if (activityEnabled()) recorder.flush(); else recorder.discard(); }, 15000);
  document.addEventListener('visibilitychange', () => {
    recorder.visibility(document.visibilityState !== 'hidden');
    if (document.visibilityState === 'hidden') recorder.flush(true); else sync();
  });
  window.addEventListener('pagehide', () => { recorder.visibility(false); recorder.flush(true); });
  window.addEventListener('pageshow', () => { recorder.visibility(document.visibilityState !== 'hidden'); sync(); });

  const controlFor = event => event.target?.closest?.('button, a[href], summary, [role="button"], select, input[type="checkbox"], input[type="radio"], input[type="range"]');
  function describe(control) {
    if (!control || control.closest('[data-activity-private], [contenteditable="true"]')) return null;
    const named = control.dataset.activity || control.getAttribute('aria-label') || control.getAttribute('title');
    if (named) return clean(named);
    const copy = (control.labels?.[0] || control).cloneNode(true);
    copy.querySelectorAll('[aria-hidden="true"], svg, input, textarea, select').forEach(node => node.remove());
    const text = node => node.nodeType === 3 ? node.textContent : [...node.childNodes].map(text).join(' ');
    return clean((['INPUT', 'SELECT'].includes(control.tagName) && !control.labels?.length ? '' : text(copy)) || control.id || control.type || control.tagName.toLowerCase());
  }
  document.addEventListener('click', event => {
    if (!event.isTrusted) return;
    const control = controlFor(event), label = describe(control);
    if (!label || control.disabled) return;
    let destination = '';
    if (control.tagName === 'A') {
      try { const url = new URL(control.href); destination = url.origin === location.origin ? url.pathname + url.hash.split('?')[0] : url.hostname; } catch { /* Invalid link. */ }
    }
    activityAction('click', label, destination);
  }, { capture: true, passive: true });
  document.addEventListener('change', event => {
    const control = controlFor(event), label = describe(control);
    if (!event.isTrusted || !label) return;
    // Record a change, never its typed or selected value. Semantic controls
    // such as translation and reading mode supply their own explicit events.
    activityAction('change', label);
  }, { capture: true, passive: true });
  let pointer = null;
  document.addEventListener('pointerdown', event => {
    if (!event.isTrusted) return;
    const target = event.target?.closest?.('canvas, [data-activity-gesture]');
    pointer = target ? { x: event.clientX, y: event.clientY, label: target.dataset.activityGesture || target.getAttribute('aria-label') || 'Scene' } : null;
  }, { capture: true, passive: true });
  document.addEventListener('pointerup', event => {
    if (pointer && Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 8) activityAction('gesture', pointer.label, 'drag');
    pointer = null;
  }, { capture: true, passive: true });
  document.addEventListener('pointercancel', () => { pointer = null; }, { passive: true });
  let scrollTimer = 0;
  window.addEventListener('scroll', () => {
    if (scrollTimer || surfaces.length) return;
    scrollTimer = setTimeout(() => {
      scrollTimer = 0;
      if (!page || surfaces.length || !activityEnabled()) return;
      const range = document.documentElement.scrollHeight - innerHeight;
      const milestone = range > innerHeight ? Math.floor(Math.min(1, scrollY / range) * 4) * 25 : 0;
      if (milestone > scrollMilestone) { scrollMilestone = milestone; activityAction('scroll', 'Page depth', `${milestone}%`); }
      const headings = [...document.querySelectorAll('main h2[id], main section[id] > header h2, main section[id] > h2, article h2[id], .generated-view .view-section-heading, .generated-view .view-closing > h2')];
      const heading = headings.filter(node => node.getBoundingClientRect().top <= innerHeight * .35 && node.getClientRects().length).pop();
      const place = heading ? clean(heading.textContent) : '';
      if (page.place !== place) { page = { ...page, place }; sync(); }
    }, 250);
  }, { passive: true });
}
